import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Input } from "@shadcn-in-nx/ui/input";
import { Loader2, Search } from "lucide-react";
import KnowledgeBaseDocGapWarning from "./KnowledgeBaseDocGapWarning";
import KnowledgeBaseDocList from "./KnowledgeBaseDocList";
import KnowledgeBaseVideoList from "./KnowledgeBaseVideoList";

export default function KnowledgeBase() {
    const { t } = useTranslation();
    const [search, setSearch] = useState("");
    const [resources, setResources] = useState({ docs: [], videos: [] });
    const [gaps, setGaps] = useState([]);
    const { call: fetchResources, isLoading } = useBluelagoon({ method: "getSupportResources" });
    const { call: fetchGaps, isLoading: gapsLoading } = useBluelagoon({ method: "getDocGaps" });

    const loadData = useCallback(async () => {
        const [resR, gapR] = await Promise.all([
            fetchResources().catch(() => null),
            fetchGaps({ resolved: false }).catch(() => null),
        ]);
        if (resR?.data) setResources(resR.data);
        if (gapR?.data?.docGaps) setGaps(gapR.data.docGaps);
    }, [fetchResources, fetchGaps]);

    useEffect(() => { loadData(); }, [loadData]);

    const q = search.toLowerCase();
    const filteredDocs = useMemo(() =>
        resources.docs.filter((d) => d.title.toLowerCase().includes(q) || d.description?.toLowerCase().includes(q)),
    [resources.docs, q]);
    const filteredVideos = useMemo(() =>
        resources.videos.filter((v) => v.title.toLowerCase().includes(q) || v.description?.toLowerCase().includes(q)),
    [resources.videos, q]);

    if (isLoading || gapsLoading) {
        return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
    }

    return (
        <div className="space-y-6">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder={t("admin.support.searchResources", "Search docs & videos...")}
                    className="pl-9" />
            </div>
            <KnowledgeBaseDocGapWarning gaps={gaps} />
            <KnowledgeBaseDocList docs={filteredDocs} />
            <KnowledgeBaseVideoList videos={filteredVideos} />
        </div>
    );
}
